<!--
  DecorationApprovalCard.vue -- the decoration detail page's Approval card.

  Jake, 2026-10-27: "digitization and approval are two separate things",
  separate but related in the app IA. DecorationDigitizationCard.vue is the
  digitization half; this is the approval half.

  What it shows, per size
  =======================
  Each client's approval of the size, from the decorations payload:
  approved by the client themself (`approved_clients` minus the ones the
  distributor approved on their behalf), approved by a team member's
  attestation of the file ("Approved by <member> on behalf of <client>";
  `team_attestations`), changes requested
  (`changes_requested_clients`), and -- through SizeApprovalOverride, the
  library's per-size approve-on-behalf control, reused unchanged -- awaiting
  and approved-on-behalf. A size with none of these says it was not sent.

  The link to digitization (embroidery only)
  ==========================================
  Jake, 2026-10-27: an EMBROIDERY size opens for approval once a digitized
  version exists -- the client approves the stitched proof, not the raster.
  Until then the row says so and the approve-on-behalf action is withheld
  (the statuses still show). Every other method (screen print, DTF, ...)
  stays approvable as before. The state comes from the API's derived
  `digitization.state`; an older payload without it falls back to the
  card's `digitized` readiness flag. NOTE: this is a UI gate; the
  approve-on-behalf endpoints do not enforce it.

  Who may approve on the client's behalf: an override holder
  (distributor.decoration_library.override), the capability the endpoint
  enforces. The approval is attributed to the caller; a team member other
  than the caller cannot be named here (no per-size team attestation
  exists; see the PR's escalation).

  A team member's upload-time statement that the file was already approved
  (and Retract, for those who may) is TeamAttestationList.vue, shown above
  the sizes (Bug Intake #578 follow-up). A retract re-reads the cards like an
  override does (`retracted`). So does retracting an approval made on a
  client's behalf (SizeApprovalOverride; Bug Intake #732).
-->
<template>
    <section
        class="dac"
        data-testid="dla-approval-card"
    >
        <div class="dac__head">
            <h2 class="dac__title">
                Approval
            </h2>
            <span class="dac__sub">
                Each client's approval of each size
            </span>
        </div>

        <TeamAttestationList
            :artwork-id="artworkId"
            @retracted="(e) => $emit('retracted', e)"
        />

        <div
            v-if="breakdownLabel"
            class="dac__breakdown"
        >
            <p
                class="dac__breakdown-total"
                data-testid="dla-approval-breakdown"
            >{{ breakdownLabel }}</p>
            <ul
                v-if="breakdownClients.length"
                class="dac__breakdown-clients"
            >
                <li
                    v-for="c in breakdownClients"
                    :key="c.id"
                    :data-testid="`dla-approval-breakdown-client-${c.id}`"
                >{{ c.text }}</li>
            </ul>
        </div>

        <p
            v-if="!cards.length"
            class="dac__muted"
            data-testid="dla-approval-empty"
        >
            Nothing to approve yet: this artwork has no sizes.
        </p>

        <div
            v-for="card in cards"
            :key="card.id"
            class="dac__row"
            :data-testid="`dla-approval-row-${card.id}`"
        >
            <div class="dac__row-main">
                <span class="dac__method">{{ card.methodLabel }}</span>
                <span class="dac__dims">{{ dimsLabel(card) }}</span>
                <span
                    v-if="approvedLabel(card)"
                    class="dac__chip dac__chip--approved"
                    :data-testid="`dla-card-approved-${card.id}`"
                >{{ approvedLabel(card) }}</span>
                <span
                    v-for="c in changesRequested(card)"
                    :key="`c-${c.id}`"
                    class="dac__chip dac__chip--changes"
                    :data-testid="`dla-approval-changes-${card.id}-${c.id}`"
                >Changes requested by {{ c.name }}</span>
            </div>

            <p
                v-if="waitsOnDigitizing(card)"
                class="dac__gate"
                :data-testid="`dla-approval-gate-${card.id}`"
            >Approval opens once this size is digitized.</p>

            <SizeApprovalOverride
                :card="card"
                :can-override="canOverride && !waitsOnDigitizing(card)"
                :clients="clients"
                :artwork-client-ids="artworkClientIds"
                @overridden="(e) => $emit('overridden', e)"
                @retracted="(e) => $emit('retracted', e)"
            />

            <p
                v-if="hasNoApprovalState(card)"
                class="dac__muted"
                :data-testid="`dla-approval-none-${card.id}`"
            >Not sent to a client for approval yet.</p>
        </div>
    </section>
</template>

<script>
import SizeApprovalOverride from './SizeApprovalOverride.vue'
import TeamAttestationList from './TeamAttestationList.vue'
import { approvalBreakdownLabel, approvalBreakdownClientLines } from './statusAxes'

// The methods whose approval waits on a digitized version.
const DIGITIZE_BEFORE_APPROVAL = new Set(['embroidery'])

const list = (v) => (Array.isArray(v) ? v.filter(Boolean) : [])

export default {
    name: 'DecorationApprovalCard',
    components: { SizeApprovalOverride, TeamAttestationList },
    props: {
        /** The artwork, for its team members' statements (null: none shown). */
        artworkId: {
            type: [Number, String],
            default: null,
        },
        /** The page's flattened decoration cards (allDecorationCards). */
        cards: {
            type: Array,
            default: () => [],
        },
        /** Caller holds distributor.decoration_library.override. */
        canOverride: {
            type: Boolean,
            default: false,
        },
        /** The clients a size may be approved for on their behalf. */
        clients: {
            type: Array,
            default: () => [],
        },
        /** The artwork's own clients, to preselect. */
        artworkClientIds: {
            type: Array,
            default: () => [],
        },
        /**
         * The artwork's ``approval_breakdown`` (backend roll-up of the same
         * per-size states the library pills show); null when there is none.
         */
        breakdown: {
            type: Object,
            default: null,
        },
    },
    emits: ['overridden', 'retracted'],
    computed: {
        breakdownLabel() {
            return approvalBreakdownLabel(this.breakdown)
        },
        breakdownClients() {
            return approvalBreakdownClientLines(this.breakdown)
        },
    },
    methods: {
        dimsLabel(card) {
            const fmt = (v) => (v != null ? Number(v).toFixed(2) : '?')
            return `${fmt(card.width_in)}" x ${fmt(card.height_in)}"`
        },
        waitsOnDigitizing(card) {
            if (!DIGITIZE_BEFORE_APPROVAL.has(card?.methodSlug)) return false
            const state = card?.digitization?.state
            if (state) return state !== 'digitized'
            return card?.digitized !== true
        },
        changesRequested(card) {
            return list(card?.changes_requested_clients)
        },
        approvedLabel(card) {
            // Only the clients who approved the size themselves, and the
            // ones a team member vouched for. The ones the distributor
            // approved on their behalf are named by SizeApprovalOverride,
            // attributed to whoever gave it.
            const onBehalf = new Set(
                list(card?.approval_overrides).map((o) => o.client_id),
            )
            // A team member's attestation reads "by <member> on behalf of
            // <client>" (Jake, 2026-10-29); the client's own stays "by
            // <client>".
            const attested = new Map(
                list(card?.team_attestations).map((t) => [t.client_id, t]),
            )
            const own = []
            const vouched = []
            for (const c of list(card?.approved_clients)) {
                if (onBehalf.has(c.id) || !c.name) continue
                const t = attested.get(c.id)
                if (t) vouched.push(`Approved by ${t.by_name || 'a team member'} on behalf of ${c.name}`)
                else own.push(c.name)
            }
            const parts = own.length ? [`Approved by ${own.join(', ')}`] : []
            return [...parts, ...vouched].join('; ')
        },
        hasNoApprovalState(card) {
            // A size whose approval waits on digitizing already says so;
            // "not sent to a client" would be a second, competing line.
            if (this.waitsOnDigitizing(card)) return false
            return (
                !list(card?.approved_clients).length &&
                !list(card?.awaiting_clients).length &&
                !list(card?.changes_requested_clients).length &&
                !list(card?.approval_overrides).length &&
                !(this.canOverride && !this.waitsOnDigitizing(card))
            )
        },
    },
}
</script>

<style scoped>
.dac {
    background: #ffffff;
    border: 1px solid var(--tf-border, #e0e0e0);
    border-radius: 10px;
    padding: 14px 16px;
    margin-bottom: 26px;
    display: flex;
    flex-direction: column;
    gap: 10px;
}
.dac__head {
    display: flex;
    align-items: baseline;
    flex-wrap: wrap;
    gap: 9px;
}
.dac__title {
    font-size: 16px;
    font-weight: 500;
    color: var(--tf-fg-1, #212121);
    font-family: var(--tf-font-display, sans-serif);
    margin: 0;
}
.dac__sub,
.dac__muted {
    font-size: 12.5px;
    color: var(--tf-fg-4, #616161);
    margin: 0;
}
.dac__breakdown {
    margin: 0 0 8px;
}
.dac__breakdown-total {
    font-size: 13px;
    font-weight: 600;
    margin: 0;
}
.dac__breakdown-clients {
    list-style: none;
    margin: 2px 0 0;
    padding: 0;
    font-size: 12.5px;
    color: var(--tf-fg-4, #616161);
}
.dac__row {
    border-top: 1px solid var(--tf-border, #e0e0e0);
    padding-top: 10px;
    display: flex;
    flex-direction: column;
    gap: 6px;
}
.dac__row-main {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
}
.dac__method {
    display: inline-flex;
    align-items: center;
    background: #ede7f6;
    color: #5e35b1;
    font-size: 11.5px;
    font-weight: 500;
    padding: 3px 9px;
    border-radius: var(--tf-radius-pill, 9999px);
}
.dac__dims {
    font-size: 13px;
    color: var(--tf-fg-2, #424242);
}
.dac__chip {
    display: inline-flex;
    align-items: center;
    font-size: 11px;
    font-weight: 500;
    padding: 2px 8px;
    border-radius: var(--tf-radius-pill, 9999px);
}
.dac__chip--approved {
    background: #e6f7ed;
    color: #12805a;
}
.dac__chip--changes {
    background: #fdecea;
    color: #b42318;
}
.dac__gate {
    margin: 0;
    font-size: 12.5px;
    color: #a15c00;
}
</style>
